import { useCallback, useEffect, useRef, useState } from 'react';
import type { InstitutionCorrespondence } from '../../../types';
import type { WorkflowDocument } from '@/lib/services/correspondences/workflows/workflow-types';
import { convertAttachmentToWorkflowDocument } from '../utils/document-transform';

interface UseDraftManagementProps {
  existingDraftReply: InstitutionCorrespondence | undefined;
  pendingAttachments: WorkflowDocument[];
  onSetPendingAttachments: (attachments: WorkflowDocument[]) => void;
}

export function useDraftManagement({
  existingDraftReply,
  pendingAttachments: _pendingAttachments,
  onSetPendingAttachments
}: UseDraftManagementProps) {
  // `clearedDraftId` lets the consumer mark the active backend draft as locally
  // cleared (e.g. just-sent) before the next refetch removes it from props.
  const [clearedDraftId, setClearedDraftId] = useState<string | null>(null);

  const isCleared =
    existingDraftReply !== undefined &&
    clearedDraftId === String(existingDraftReply.id);

  const currentDraftId =
    !existingDraftReply || isCleared ? null : String(existingDraftReply.id);

  const draftContent =
    !currentDraftId || !existingDraftReply
      ? ''
      : existingDraftReply.fragments?.mainContent ||
        existingDraftReply.body ||
        existingDraftReply.content ||
        '';

  const clearDraft = useCallback(() => {
    if (existingDraftReply) setClearedDraftId(String(existingDraftReply.id));
  }, [existingDraftReply]);

  // Side effect: notify parent of attachment changes when the active draft
  // identity flips. The ref guards against re-firing on unrelated renders.
  const lastSyncedDraftIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentDraftId === lastSyncedDraftIdRef.current) return;
    const previous = lastSyncedDraftIdRef.current;
    lastSyncedDraftIdRef.current = currentDraftId;

    if (
      currentDraftId &&
      existingDraftReply &&
      Array.isArray(existingDraftReply.attachments)
    ) {
      const draftAttachments = existingDraftReply.attachments
        .map((att) => convertAttachmentToWorkflowDocument(att))
        .filter((att): att is WorkflowDocument => att !== null);
      onSetPendingAttachments(draftAttachments);
    } else if (currentDraftId === null && previous !== null) {
      onSetPendingAttachments([]);
    }
  }, [currentDraftId, existingDraftReply, onSetPendingAttachments]);

  return {
    currentDraftId,
    draftContent,
    clearDraft
  };
}
